import { OAuthAccountManager } from '@talon/web/src/components/ui/OAuthAccountManager';
import { useProjectStore, useUserStore } from '@talon/web/src/context';
import { useGoogleDrivePicker } from '@talon/web/src/hooks/useGoogleDrivePicker';
import { Box, Button, Text } from 'grommet';
import { Google } from 'grommet-icons';
import { Asset, AssetType, TOAuthProvider, UserService } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { Divider } from './Divider';

export interface AssetCloudImportButtonsProps {
  assetType: AssetType;
  description?: string;
  disabled?: boolean;
  onImportSuccess?: (asset: Asset) => void | Promise<void>;
  onImportStart?: () => void;
  onImportEnd?: () => void;
  size?: 'small' | 'medium' | 'large';
  direction?: 'row' | 'column';
  showDivider?: boolean;
}

export const AssetCloudImportButtons = observer(
  ({
    assetType,
    description = '',
    disabled = false,
    onImportSuccess,
    onImportStart,
    onImportEnd,
    size = 'medium',
    direction = 'row',
    showDivider = true,
  }: AssetCloudImportButtonsProps) => {
    /** Context **/
    const projectStore = useProjectStore();
    const userStore = useUserStore();
    const { openPicker: openGooglePicker, isLoaded: googlePickerLoaded } = useGoogleDrivePicker();

    /** Computed **/
    const hasGoogleDrive = userStore.user?.oauth_profiles?.some((p) => p.provider === TOAuthProvider.GOOGLE);
    const hasAnyAccount = hasGoogleDrive;

    /** Methods **/

    // Helper function to get picker title based on asset type
    const getPickerTitle = (type: AssetType): string => {
      const titles: Record<AssetType, string> = {
        [AssetType.Video]: 'Select video files',
        [AssetType.Image]: 'Select image files',
        [AssetType.Audio]: 'Select audio files',
        [AssetType.Document]: 'Select document files',
      };
      return titles[type];
    };

    // Helper function to detect asset type from MIME type
    const detectAssetTypeFromMime = (mimeType: string): AssetType | null => {
      const mime = mimeType.toLowerCase();
      if (mime.startsWith('video/')) return AssetType.Video;
      if (mime.startsWith('image/')) return AssetType.Image;
      if (mime.startsWith('audio/')) return AssetType.Audio;
      if (
        mime.startsWith('application/pdf') ||
        mime.startsWith('text/') ||
        mime.includes('document') ||
        mime.includes('word')
      )
        return AssetType.Document;
      return null;
    };

    // Get MIME type filters for the selected asset type
    const getMimeTypeFilters = (type: AssetType): string[] => {
      const mimeTypeFilters: Record<AssetType, string[]> = {
        [AssetType.Video]: [
          'video/mp4',
          'video/quicktime',
          'video/x-msvideo',
          'video/x-matroska',
          'video/webm',
          'video/mpeg',
          'video/x-flv',
        ],
        [AssetType.Image]: [
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/webp',
          'image/svg+xml',
          'image/bmp',
          'image/tiff',
        ],
        [AssetType.Audio]: [
          'audio/mpeg',
          'audio/mp3',
          'audio/wav',
          'audio/x-wav',
          'audio/aac',
          'audio/x-m4a',
          'audio/flac',
          'audio/ogg',
          'audio/webm',
        ],
        [AssetType.Document]: [
          'application/pdf',
          'text/plain',
          'text/csv',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
      };
      return mimeTypeFilters[type];
    };

    const handleGoogleDriveImport = async () => {
      if (!hasGoogleDrive || !googlePickerLoaded) {
        toast.error('Please connect your Google Drive account in your profile settings first');
        return;
      }

      const googleProfile = userStore.user?.oauth_profiles?.find((p) => p.provider === TOAuthProvider.GOOGLE);
      if (!googleProfile) {
        toast.error('Google Drive not connected');
        return;
      }

      // Retrieve the access token from the backend (with automatic refresh if stale)
      let accessToken: string;
      try {
        const tokenResponse = await UserService.getOAuthAccessToken(userStore.user.id, TOAuthProvider.GOOGLE);
        accessToken = tokenResponse.accessToken;
      } catch (error: unknown) {
        if ((error as { status?: number })?.status === 401) {
          toast.error(
            'Your Google Drive connection has expired. Please disconnect and reconnect in your profile settings.'
          );
        } else {
          toast.error('Failed to retrieve Google Drive access token');
        }
        console.error('Error getting OAuth access token:', error);
        return;
      }

      // Fetch the Google Picker API key from backend
      const pickerApiKey = await projectStore.getPickerApiKey();
      if (!pickerApiKey) {
        toast.error('Failed to load Google Drive picker');
        return;
      }

      openGooglePicker(
        pickerApiKey,
        accessToken,
        async (files) => {
          if (files.length === 0) return;

          const file = files[0];

          // Auto-detect asset type from MIME type if not video (video is always explicit)
          const detectedType = detectAssetTypeFromMime(file.mimeType);
          const finalAssetType = detectedType || assetType;

          if (!detectedType) {
            toast.warning(`Could not auto-detect file type for ${file.mimeType}, using selected type: ${assetType}`);
          } else if (detectedType !== assetType && assetType !== AssetType.Video) {
            // Only show type change notification for non-video assets
            toast.info(`Auto-detected file as ${detectedType} (selected: ${assetType})`);
          }

          onImportStart?.();
          try {
            const asset = await projectStore.createAssetFromDrive(file.id, file.name, finalAssetType, description);
            if (!asset) {
              toast.error('Error importing asset from Google Drive');
            } else {
              toast.success('Asset imported successfully from Google Drive');
              await onImportSuccess?.(asset);
            }
          } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : 'Error importing asset from Google Drive');
          } finally {
            onImportEnd?.();
          }
        },
        getMimeTypeFilters(assetType),
        getPickerTitle(assetType)
      );
    };

    /** Render **/

    return (
      <Box gap="small">
        {showDivider && (
          <Box>
            <Divider text="OR IMPORT FROM" />
          </Box>
        )}

        {/* Show OAuth connection manager if no accounts connected */}
        {!hasAnyAccount ? (
          <Box gap="medium">
            <Box>
              <Text size="small" textAlign="center">
                Connect your cloud storage accounts to import files
              </Text>
            </Box>
            <Box>
              <OAuthAccountManager compact />
            </Box>
          </Box>
        ) : (
          <>
            {/* Show import buttons and connection prompts */}
            <Box direction={direction} gap="small">
              {hasGoogleDrive ? (
                <Button
                  label="Google Drive"
                  icon={<Google />}
                  onClick={handleGoogleDriveImport}
                  disabled={disabled || !googlePickerLoaded}
                  secondary
                  size={size}
                />
              ) : (
                <Box>
                  <Text size="xsmall" color="dark-4" margin={{ bottom: 'xsmall' }}>
                    Google Drive not connected
                  </Text>
                  <OAuthAccountManager compact providers={[TOAuthProvider.GOOGLE]} />
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    );
  }
);
